import React, { useEffect, useMemo, useState } from 'react';

import { Spinner } from '../components/base';
import { db } from '../lib/firebase';
import { ENDPOINT, chaveSugerida, normalizarOrigens, trechoDireto, trechoFormulario, trechoInstalacao, validarSite } from '../lib/sites';

/**
 * Sites da empresa. Cadastrar um site aqui é o que faz a captação reconhecer o
 * lead: a função lê esta coleção para descobrir de quem é a chave e quais
 * origens podem enviar. Antes isso era um mapa fixo no código da função, e
 * cliente novo dependia de deploy.
 */
function SitesView({ company, companyLabel, isAdmin }) {
  const [sites, setSites] = useState(null);
  const [edit, setEdit] = useState(null);   // { chave, rotulo, origensTexto, ativo, novo }
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState('');

  useEffect(() => {
    setSites(null);
    return db.collection('sites').where('company', '==', company)
      .onSnapshot(
        snap => setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.id.localeCompare(b.id))),
        () => setSites([]));
  }, [company]);

  const chavesEmUso = useMemo(() => (sites || []).map(s => s.id), [sites]);
  const inp = 'apple-input w-full px-3 py-2 text-sm';

  const abrirNovo = () => { setErro(''); setEdit({ chave: '', rotulo: '', origensTexto: '', ativo: true, novo: true }); };
  const abrirEdicao = (s) => {
    setErro('');
    setEdit({ chave: s.id, rotulo: s.rotulo || '', origensTexto: (s.origens || []).join('\n'), ativo: s.ativo !== false, novo: false });
  };

  const guardar = async () => {
    const { origens, invalidas } = normalizarOrigens((edit.origensTexto || '').split('\n'));
    if (invalidas.length) { setErro(`Não entendi estes endereços: ${invalidas.join(', ')}`); return; }
    const chave = (edit.chave || '').trim().toLowerCase();
    const msg = validarSite({ chave, rotulo: edit.rotulo, origens }, edit.novo ? chavesEmUso : []);
    if (msg) { setErro(msg); return; }

    setSalvando(true); setErro('');
    const agora = new Date().toISOString();
    try {
      const ref = db.collection('sites').doc(chave);
      if (edit.novo) {
        // A chave é global. Se outra empresa já tem essa chave, as regras barram
        // a escrita; a checagem aqui é só para dar recado melhor.
        const existente = await ref.get();
        if (existente.exists) { setErro(`A chave "${chave}" já está em uso.`); setSalvando(false); return; }
        await ref.set({ chave, company, rotulo: edit.rotulo.trim(), origens, ativo: !!edit.ativo, criado_em: agora, atualizado_em: agora });
      } else {
        await ref.update({ rotulo: edit.rotulo.trim(), origens, ativo: !!edit.ativo, atualizado_em: agora });
      }
      setEdit(null);
    } catch (e) {
      setErro(e?.code === 'permission-denied'
        ? 'Sem permissão para gravar. Só quem é admin desta empresa cadastra site.'
        : ('Não deu para salvar: ' + (e.message || e)));
    }
    setSalvando(false);
  };

  const copiar = async (texto, marca) => {
    try { await navigator.clipboard.writeText(texto); setCopiado(marca); setTimeout(() => setCopiado(''), 1800); } catch { /* sem área de transferência */ }
  };

  if (sites === null) return <div className="flex justify-center py-16"><Spinner /></div>;

  return <div className="max-w-2xl mx-auto space-y-4">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Sites · {companyLabel || company}</h2>
        <p className="text-xs text-gray-400 mt-0.5">Cada site tem uma chave. É ela que identifica de quem é o lead que chega.</p>
      </div>
      {isAdmin && !edit && <button onClick={abrirNovo}
        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-sm">+ Novo site</button>}
    </div>

    {edit && <div className="acard rounded-2xl p-4 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">Nome do site *</label>
          <input className={inp} autoFocus placeholder="Ex.: Site institucional" value={edit.rotulo}
            onChange={e => setEdit({ ...edit, rotulo: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">Chave *</label>
          <input className={inp + (edit.novo ? '' : ' bg-gray-50 text-gray-500')} placeholder="ex.: casa-perini"
            value={edit.chave} disabled={!edit.novo}
            onChange={e => setEdit({ ...edit, chave: e.target.value.toLowerCase() })} />
          <p className="text-[11px] text-gray-400 mt-1">
            {edit.novo ? 'Minúsculas, números e hífen. Não muda depois.' : 'A chave não muda: o script no site usa ela.'}
          </p>
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500 font-medium mb-1 block">Endereços do site, um por linha *</label>
        <textarea className={inp + ' h-24 font-mono text-xs'} value={edit.origensTexto}
          onChange={e => {
            const v = e.target.value;
            const sugerida = edit.novo && !edit.chave ? chaveSugerida(v.split('\n')[0]) : '';
            setEdit({ ...edit, origensTexto: v, ...(sugerida ? { chave: sugerida } : {}) });
          }}
          placeholder={'maximizy.com.br\nwww.maximizy.com.br'} />
        <p className="text-[11px] text-gray-400 mt-1">Só estes endereços conseguem enviar lead com esta chave. Inclua a versão com www.</p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
        <input type="checkbox" checked={!!edit.ativo} onChange={e => setEdit({ ...edit, ativo: e.target.checked })} />
        Ativo, recebendo leads
      </label>

      {erro && <p className="text-xs text-red-600">{erro}</p>}

      <div className="flex gap-2">
        <button onClick={guardar} disabled={salvando}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-full">
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
        <button onClick={() => { setEdit(null); setErro(''); }}
          className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancelar</button>
      </div>
    </div>}

    {!sites.length && !edit && <div className="acard rounded-2xl p-8 text-center">
      <p className="text-sm text-gray-500">Nenhum site cadastrado ainda.</p>
      <p className="text-xs text-gray-400 mt-1">Sem site cadastrado, formulário nenhum consegue mandar lead para cá.</p>
    </div>}

    {sites.map(s => <div key={s.id} className="acard rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-800">{s.rotulo || s.id}</h3>
            <code className="text-[11px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 font-mono">{s.id}</code>
            {s.ativo === false && <span className="text-[11px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">Pausado</span>}
          </div>
          <p className="text-xs text-gray-400 mt-1 break-all">{(s.origens || []).join('  ·  ')}</p>
        </div>
        {isAdmin && <button onClick={() => abrirEdicao(s)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex-shrink-0">Editar</button>}
      </div>

      <details className="mt-3">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Como instalar</summary>
        <div className="mt-2 space-y-3">
          <div>
            <p className="text-[11px] text-gray-500 mb-1">
              <strong className="text-gray-600">1. Rastreio da jornada.</strong> No Tag Manager, tag de HTML
              personalizado disparando em todas as páginas. O script é criado por código de propósito: o Tag
              Manager apaga atributos personalizados da tag, e a chave se perderia no caminho.
            </p>
            <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-xl p-3 overflow-x-auto font-mono">{trechoInstalacao(s.id)}</pre>
            <button onClick={() => copiar(trechoInstalacao(s.id), s.id)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1">
              {copiado === s.id ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <div>
            <p className="text-[11px] text-gray-500 mb-1">
              <strong className="text-gray-600">Fora do Tag Manager</strong>, direto no HTML, uma linha basta.
            </p>
            <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-xl p-3 overflow-x-auto font-mono">{trechoDireto(s.id)}</pre>
            <button onClick={() => copiar(trechoDireto(s.id), s.id + ':direto')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1">
              {copiado === s.id + ':direto' ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <div>
            <p className="text-[11px] text-gray-500 mb-1">
              <strong className="text-gray-600">2. Captação do formulário.</strong> Esse snippet é maior, porque
              precisa ler os campos do construtor de cada site. No começo dele, use estas duas linhas.
            </p>
            <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-xl p-3 overflow-x-auto font-mono">{trechoFormulario(s.id)}</pre>
            <button onClick={() => copiar(trechoFormulario(s.id), s.id + ':form')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1">
              {copiado === s.id + ':form' ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>
      </details>
    </div>)}

    <p className="text-[11px] text-gray-400 text-center pt-2 break-all">Endereço de recebimento: {ENDPOINT}</p>
  </div>;
}

export { SitesView };
